import { useState, useEffect, useRef } from 'react';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LightBulbIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useOperationsHandbook, type Section, type SubSectionType } from '../hooks/useOperationsHandbook';
import type { Block, Callout } from '../services/helpContentService';

import ssLogin           from '../assets/screenshots/login.png';
import ssNav             from '../assets/screenshots/nav.png';
import ssOrdersList      from '../assets/screenshots/orders_list.png';
import ssOrderDetail     from '../assets/screenshots/order_detail.png';
import ssCreateOrder     from '../assets/screenshots/create_order.png';
import ssAssignPicker    from '../assets/screenshots/assign_picker.png';
import ssCollectionOrder from '../assets/screenshots/collection_order.png';
import ssPinModal        from '../assets/screenshots/pin_modal.png';
import ssDrivers         from '../assets/screenshots/drivers.png';
import ssOrdersActions   from '../assets/screenshots/orders_actions.png';
import ssPickers         from '../assets/screenshots/pickers.png';
import ssCpoints         from '../assets/screenshots/collection_points.png';
import ssCpModal         from '../assets/screenshots/add_cp_modal.png';
import ssFees            from '../assets/screenshots/fees.png';

const SCREENSHOTS: Record<string, string> = {
    login:            ssLogin,
    nav:              ssNav,
    orders_list:      ssOrdersList,
    order_detail:     ssOrderDetail,
    create_order:     ssCreateOrder,
    assign_picker:    ssAssignPicker,
    collection_order: ssCollectionOrder,
    pin_modal:        ssPinModal,
    drivers:          ssDrivers,
    orders_actions:   ssOrdersActions,
    pickers:          ssPickers,
    cpoints:          ssCpoints,
    cpmodal:          ssCpModal,
    fees:             ssFees,
};

// ── Callout ───────────────────────────────────────────────────────────────────
function Callout({ callout }: { callout: Callout }) {
    const map = {
        tip:  { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', label: 'Tip',       Icon: LightBulbIcon,          iconColor: 'text-emerald-500' },
        warn: { bg: 'bg-amber-50 border-amber-200',     text: 'text-amber-900',  label: 'Important', Icon: ExclamationTriangleIcon, iconColor: 'text-amber-500'   },
        note: { bg: 'bg-pink-50 border-pink-200',       text: 'text-pink-900',   label: 'Note',      Icon: InformationCircleIcon,   iconColor: 'text-[#e6186c]'   },
    };
    const s = map[callout.type];
    return (
        <div className={`flex gap-3 rounded-lg border px-4 py-3 text-sm ${s.bg}`}>
            <s.Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${s.iconColor}`} />
            <p className={s.text}><span className="font-semibold">{s.label}: </span>{callout.text}</p>
        </div>
    );
}

// ── Screenshot ────────────────────────────────────────────────────────────────
function Screenshot({ ssKey, alt }: { ssKey: string; alt: string }) {
    const src = SCREENSHOTS[ssKey];
    if (!src) return null;
    return (
        <div className="overflow-hidden rounded-xl border border-stone-200 shadow-sm">
            <div className="bg-[#e6186c] px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1">
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                    <div className="h-2 w-2 rounded-full bg-white/30" />
                </div>
                <p className="text-xs font-medium text-white/90 ml-1">{alt}</p>
            </div>
            <img src={src} alt={alt} className="w-full object-contain bg-stone-50" />
        </div>
    );
}

// ── Block renderer ────────────────────────────────────────────────────────────
function RenderBlock({ block }: { block: Block }) {
    if (block.kind === 'body') return (
        <p className="text-sm text-stone-600 leading-relaxed">{block.text}</p>
    );

    if (block.kind === 'callout') return <Callout callout={block.callout} />;

    if (block.kind === 'bullets') return (
        <ul className="space-y-1.5">
            {block.items.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-stone-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#e6186c] flex-shrink-0" />
                    {item}
                </li>
            ))}
        </ul>
    );

    if (block.kind === 'steps') return (
        <ol className="space-y-2">
            {block.items.map((s, i) => (
                <li key={i} className="flex gap-3">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e6186c] text-xs font-bold text-white">
                        {i + 1}
                    </span>
                    <div className="pt-0.5">
                        <span className="text-sm font-medium text-stone-800">{s.action}</span>
                        {s.detail && <p className="mt-0.5 text-xs text-stone-500 italic">{s.detail}</p>}
                    </div>
                </li>
            ))}
        </ol>
    );

    if (block.kind === 'checklist') return (
        <div className="space-y-1.5">
            {block.items.map((item, i) => (
                <ChecklistItem key={i} text={item} />
            ))}
        </div>
    );

    if (block.kind === 'statuses') return (
        <div className="overflow-hidden rounded-lg border border-stone-200">
            <table className="w-full text-xs">
                <thead>
                <tr className="bg-[#e6186c]">
                    {['Status', 'What it means', 'Action required'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-white">{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {block.rows.map(([st, desc, act], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-stone-50' : 'bg-white'}>
                        <td className="px-3 py-2 font-mono font-semibold text-stone-800">{st}</td>
                        <td className="px-3 py-2 text-stone-600">{desc}</td>
                        <td className="px-3 py-2 text-stone-600">{act}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );

    return null;
}

function ChecklistItem({ text }: { text: string }) {
    const [checked, setChecked] = useState(false);
    return (
        <button
            onClick={() => setChecked(c => !c)}
            className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                checked ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-white hover:bg-stone-50'
            }`}
        >
            <div className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-all ${
                checked ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'
            }`}>
                {checked && <CheckCircleIcon className="h-3 w-3 text-white" />}
            </div>
            <span className={`leading-snug transition-all ${checked ? 'text-stone-400 line-through' : 'text-stone-700'}`}>
                {text}
            </span>
        </button>
    );
}

// ── Sub-section accordion ─────────────────────────────────────────────────────
function SubSection({ sub, defaultOpen = false }: {
    sub: SubSectionType;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-stone-50"
            >
                <div className="flex items-center gap-3">
                    {sub.number && (
                        <span className="flex h-6 w-10 items-center justify-center rounded-md bg-stone-100 text-xs font-bold text-stone-500">
                            {sub.number}
                        </span>
                    )}
                    <span className="font-semibold text-stone-800">{sub.title}</span>
                </div>
                {open
                    ? <ChevronDownIcon className="h-4 w-4 text-stone-400" />
                    : <ChevronRightIcon className="h-4 w-4 text-stone-400" />
                }
            </button>
            {open && (
                <div className="space-y-4 border-t border-stone-100 px-5 py-5">
                    {sub.screenshot && <Screenshot ssKey={sub.screenshot} alt={sub.title} />}
                    {sub.blocks.map((b, i) => <RenderBlock key={i} block={b} />)}
                </div>
            )}
        </div>
    );
}

// ── Main section card ─────────────────────────────────────────────────────────
function SectionCard({ section }: { section: Section }) {
    const [open, setOpen] = useState(true);
    const ref = useRef<HTMLDivElement>(null);
    const hasContent = section.blocks.length > 0 || (section.sub && section.sub.length > 0);

    return (
        <div ref={ref} id={section.id} className="scroll-mt-6">
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <button
                    onClick={() => setOpen(o => !o)}
                    className="flex w-full items-center justify-between px-6 py-5 text-left transition-colors hover:bg-stone-50"
                >
                    <div className="flex items-center gap-4">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e6186c] text-sm font-bold text-white shadow-sm">
                            {section.number}
                        </span>
                        <h2 className="text-base font-semibold text-stone-900">{section.title}</h2>
                    </div>
                    <div className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                        open ? 'bg-[#e6186c] text-white' : 'bg-stone-100 text-stone-400'
                    }`}>
                        {open
                            ? <ChevronDownIcon className="h-4 w-4" />
                            : <ChevronRightIcon className="h-4 w-4" />
                        }
                    </div>
                </button>

                {open && hasContent && (
                    <div className="border-t border-stone-100 px-6 py-6 space-y-5">
                        {section.screenshot && (
                            <Screenshot ssKey={section.screenshot} alt={section.title} />
                        )}
                        {section.blocks.map((b, i) => <RenderBlock key={i} block={b} />)}
                        {section.sub && section.sub.length > 0 && (
                            <div className="space-y-3">
                                {section.sub.map((sub, i) => (
                                    <SubSection key={sub.id} sub={sub} defaultOpen={i === 0} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HelpPage() {
    const { sections, loading, error } = useOperationsHandbook();
    const [search, setSearch] = useState('');
    const [activeId, setActiveId] = useState('');

    useEffect(() => {
        const onScroll = () => {
            for (const s of [...sections].reverse()) {
                const el = document.getElementById(s.id);
                if (el && el.getBoundingClientRect().top <= 120) {
                    setActiveId(s.id); return;
                }
            }
            setActiveId('');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [sections]);

    const filtered = search.trim().length > 1
        ? sections.filter(s =>
            s.title.toLowerCase().includes(search.toLowerCase()) ||
            s.blocks.some(b => 'text' in b && b.text?.toLowerCase().includes(search.toLowerCase())) ||
            s.sub?.some(sub =>
                sub.title.toLowerCase().includes(search.toLowerCase()) ||
                sub.blocks.some(b => 'text' in b && b.text?.toLowerCase().includes(search.toLowerCase()))
            )
        )
        : sections;

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <p className="text-sm text-stone-500">Loading Operations Handbook…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <p className="text-sm text-red-600">Failed to load Operations Handbook: {error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl font-semibold text-stone-900">Operations Handbook</h1>
                </div>
                <p className="text-sm text-stone-500">Step-by-step instructions for using the Admin Portal</p>
            </div>

            <div className="flex gap-6">
                {/* Sticky sidebar */}
                <aside className="hidden lg:block w-56 flex-shrink-0">
                    <div className="sticky top-24 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
                        <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-widest text-stone-400">Sections</p>
                        <nav className="space-y-0.5">
                            {sections.map(s => (
                                <button
                                    key={s.id}
                                    onClick={() => scrollTo(s.id)}
                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-all ${
                                        activeId === s.id
                                            ? 'bg-[#fde8f0] font-medium text-[#e6186c]'
                                            : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                                    }`}
                                >
                                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-xs font-bold transition-all ${
                                        activeId === s.id ? 'bg-[#e6186c] text-white' : 'bg-stone-100 text-stone-500'
                                    }`}>
                                        {s.number}
                                    </span>
                                    <span className="leading-snug">{s.title}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Main content */}
                <div className="flex-1 min-w-0 space-y-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search the guide..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full rounded-xl border border-stone-300 bg-white py-3 pl-10 pr-4 text-sm shadow-sm placeholder:text-stone-400 focus:border-[#e6186c] focus:outline-none focus:ring-2 focus:ring-[#e6186c]/20"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400 hover:text-stone-600"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center">
                            <p className="text-stone-500">No sections match your search.</p>
                        </div>
                    ) : (
                        filtered.map(s => <SectionCard key={s.id} section={s} />)
                    )}
                </div>
            </div>
        </div>
    );
}